// Parity P1 wave 1: the task's fields (hybrid design §3.1 "fields with inline editors for every
// editable type (formula/rollup read-only), coloured chips, URL open, long text overlay, `*` with
// its stage (on create / on complete), history popover; Agent ▾ group for `audience=agent`";
// §3.5 "empty fields behind '+ Set a field'").
//
// In the properties column: the fields the layout does not place in the body. A field shows
// when it has a value, is required, is marked "always show", or was just revealed; the rest sit
// behind "+ Set a field". Fields for agents (`audience=agent`) are one folded "Agent fields"
// group. A field the server refused a completion over (`required_missing`) is highlighted.
import {
  Button,
  Checkbox,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TagChip,
  type TagChipColor,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  BotIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  HistoryIcon,
  Maximize2Icon,
  PlusIcon,
} from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { CompanyAccess } from "../../company";
import { useTaskHost } from "../task-layout/host-context";
import { useTaskWrites } from "../task-writes";
import type { FieldDef, TaskDetail } from "../view-engine";
import { taskQuery, workKeys } from "../work-queries";
import { type FieldRule, fieldHistoryQuery } from "./queries";

const TAG_COLORS = new Set<string>([
  "zinc",
  "violet",
  "sky",
  "emerald",
  "amber",
  "rose",
  "indigo",
  "teal",
  "orange",
]);
const chipColor = (c: string | undefined): TagChipColor =>
  c && TAG_COLORS.has(c) ? (c as TagChipColor) : "zinc";

const READ_ONLY = new Set(["formula", "rollup"]);
const LONG_TEXT = 80;

const empty = (v: unknown) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** A field value as words (history lines, read-only cells). */
export function fieldText(
  field: FieldDef,
  value: unknown,
  seatName?: (id: string) => string,
): string {
  if (empty(value)) return "—";
  const choice = (k: unknown) => field.options.choices?.find((c) => c.key === k)?.name ?? String(k);
  if (field.type === "select") return choice(value);
  if (field.type === "multi_select" && Array.isArray(value)) return value.map(choice).join(", ");
  if (field.type === "checkbox") return value === true ? "Yes" : "No";
  if (field.type === "seat" && typeof value === "string") return seatName?.(value) ?? value;
  if (Array.isArray(value)) return value.join(", ");
  if (field.type === "currency" && typeof value === "object") {
    const m = value as { amount_micros?: number; currency?: string };
    return `${m.currency ?? field.options.currency ?? ""} ${((m.amount_micros ?? 0) / 1e6).toLocaleString()}`.trim();
  }
  if (field.type === "date" && typeof value === "string") return value.slice(0, 10);
  return String(value);
}

/** The text an input edits for a stored value (a date's day, a currency's amount). */
export function asDraft(field: Pick<FieldDef, "type">, value: unknown): string {
  if (empty(value)) return "";
  if (field.type === "date" && typeof value === "string") return value.slice(0, 10);
  if (field.type === "currency" && typeof value === "object")
    return String(((value as { amount_micros?: number }).amount_micros ?? 0) / 1e6);
  return String(value);
}

/** The value to store for an input's text (null: clear; undefined: unreadable, keep). */
export function fromDraft(
  field: Pick<FieldDef, "type" | "options">,
  value: unknown,
  draft: string,
): unknown {
  const t = draft.trim();
  if (t === "") return null;
  if (field.type === "number") return Number.isFinite(Number(t)) ? Number(t) : undefined;
  if (field.type === "currency") {
    const n = Number(t);
    if (!Number.isFinite(n)) return undefined;
    const was = (value ?? {}) as { currency?: string };
    return {
      amount_micros: Math.round(n * 1e6),
      currency: field.options.currency ?? was.currency ?? "USD",
    };
  }
  return t;
}

/** The required stage as words, or null. */
function stageOf(rule: FieldRule | undefined): string | null {
  switch (rule?.required_on) {
    case "create":
      return "Required when the task is created";
    case "complete":
      return "Required to complete the task";
    case "both":
      return "Required on create and to complete";
    default:
      return null;
  }
}

/** One field: its label (with `*` and the history) and its editor. */
export function FieldLine({
  api,
  company,
  task,
  field,
  canEdit,
  layout = "row",
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  field: FieldDef;
  canEdit: boolean;
  /** `row`: label left (properties column); `block`: label above (the work pane). */
  layout?: "row" | "block";
}) {
  const { rules, missing, people } = useTaskHost();
  const writes = useTaskWrites(api, company);
  const id = useId();
  const rule = rules.get(field.id);
  const stage = stageOf(rule);
  const isMissing = missing.has(field.id) && empty(task.fields[field.id]);
  return (
    <div
      data-slot="task-field"
      data-field={field.name}
      data-missing={isMissing || undefined}
      className={cn(
        layout === "row"
          ? "grid min-h-8 grid-cols-[88px_minmax(0,1fr)] items-center gap-2"
          : "flex flex-col gap-1",
        isMissing && "-mx-1 rounded-md px-1 ring-2 ring-danger",
      )}
    >
      <span className="flex min-w-0 items-center gap-0.5">
        <label htmlFor={id} className="min-w-0 truncate text-xs text-muted-foreground">
          {field.name}
        </label>
        {stage ? (
          <Tooltip>
            <TooltipTrigger
              render={<span className="text-xs text-danger-text" data-slot="field-required" />}
            >
              <span aria-hidden="true">*</span>
              <span className="sr-only">{stage}</span>
            </TooltipTrigger>
            <TooltipContent>{stage}</TooltipContent>
          </Tooltip>
        ) : null}
        <FieldHistoryButton api={api} co={company.id} taskId={task.id} field={field} />
      </span>
      {field.type === "relation" ? (
        <RelationEditor
          id={id}
          api={api}
          co={company.id}
          task={task}
          field={field}
          editable={canEdit}
          onSave={(value) => writes.setField(task.id, field.id, value)}
        />
      ) : (
        <FieldEditor
          id={id}
          field={field}
          value={task.fields[field.id]}
          editable={canEdit && !READ_ONLY.has(field.type)}
          seats={people.all.filter((p) => !p.closed)}
          seatName={(s) => people.of(s).name}
          onSave={(value) => writes.setField(task.id, field.id, value)}
        />
      )}
    </div>
  );
}

/**
 * A relation field: the related tasks as removable chips and "+" to find another (the task
 * search, limited to the field's related board when it names one). The value is task ids.
 */
function RelationEditor({
  id,
  api,
  co,
  task,
  field,
  editable,
  onSave,
}: {
  id: string;
  api: Api;
  co: string;
  task: TaskDetail;
  field: FieldDef;
  editable: boolean;
  onSave: (v: unknown) => void;
}) {
  const raw = task.fields[field.id];
  const ids = Array.isArray(raw) ? (raw as string[]) : [];
  // The related tasks' titles (cached task reads; one the caller cannot see stays "A task").
  const related = useQueries({ queries: ids.map((t) => taskQuery(api, co, t)) });
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const term = q.trim();
  const board = field.options.relation_board_id;
  const results = useQuery({
    queryKey: [...workKeys.search(co, term), "relation", board ?? null],
    queryFn: () =>
      api.client.tasks.search({
        params: { co },
        body: { query: term, limit: 20, ...(board ? { board_ids: [board] } : {}) },
      }),
    enabled: open && term.length > 0,
    staleTime: 10_000,
  });
  const options = (results.data?.data ?? [])
    .filter((t) => t.id !== task.id && !ids.includes(t.id))
    .slice(0, 6);
  const name = (t: string) => {
    const hit = related.find((r) => r.data?.id === t)?.data;
    return hit ? `#${hit.number} ${hit.title}` : "A task";
  };
  return (
    <span id={id} className="flex min-w-0 flex-wrap items-center gap-1">
      {ids.map((t) => (
        <span
          key={t}
          className="inline-flex max-w-full items-center gap-0.5 rounded-sm bg-muted px-1.5 text-xs"
        >
          <span className="min-w-0 truncate">{name(t)}</span>
          {editable ? (
            <button
              type="button"
              aria-label={`Remove ${name(t)} from ${field.name}`}
              className="text-muted-foreground hover:text-foreground"
              onClick={() => {
                const next = ids.filter((x) => x !== t);
                onSave(next.length ? next : null);
              }}
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
      {!editable && ids.length === 0 ? (
        <span className="text-sm text-muted-foreground">—</span>
      ) : null}
      {editable ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button variant="ghost" size="icon-xs" aria-label={`Add a task to ${field.name}`} />
            }
          >
            <PlusIcon aria-hidden="true" />
          </PopoverTrigger>
          <PopoverContent className="w-72">
            <Input
              aria-label={`Search tasks for ${field.name}`}
              placeholder="Search tasks by title or #number…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && options[0]) {
                  e.preventDefault();
                  onSave([...ids, options[0].id]);
                  setQ("");
                  setOpen(false);
                }
              }}
            />
            {term ? (
              <ul aria-label="Matching tasks" className="m-0 mt-1 flex list-none flex-col p-0">
                {options.length ? (
                  options.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                        onClick={() => {
                          onSave([...ids, t.id]);
                          setQ("");
                          setOpen(false);
                        }}
                      >
                        <span className="text-xs text-muted-foreground tabular-nums">
                          #{t.number}
                        </span>
                        <span className="min-w-0 truncate">{t.title}</span>
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="px-2 py-1.5 text-sm text-muted-foreground">
                    {results.isPending ? "Searching…" : "No task matches."}
                  </li>
                )}
              </ul>
            ) : null}
          </PopoverContent>
        </Popover>
      ) : null}
    </span>
  );
}

function FieldEditor({
  id,
  field,
  value,
  editable,
  seats,
  seatName,
  onSave,
}: {
  id: string;
  field: FieldDef;
  value: unknown;
  editable: boolean;
  seats: { seatId: string; name: string }[];
  seatName: (id: string) => string;
  onSave: (v: unknown) => void;
}) {
  const [draft, setDraft] = useState(asDraft(field, value));
  // biome-ignore lint/correctness/useExhaustiveDependencies: the stored value resets the draft.
  useEffect(() => setDraft(asDraft(field, value)), [value]);
  if (!editable)
    return (
      <span className="min-w-0 truncate text-sm text-muted-foreground">
        {fieldText(field, value, seatName)}
        {field.type === "formula" || field.type === "rollup" ? " (computed)" : ""}
      </span>
    );
  if (field.type === "select") {
    const chosen = field.options.choices?.find((c) => c.key === value);
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        {chosen ? <TagChip name={chosen.name} color={chipColor(chosen.color)} /> : null}
        <NativeSelect
          id={id}
          size="sm"
          className={chosen ? "w-7 opacity-60" : undefined}
          aria-label={field.name}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onSave(e.target.value || null)}
        >
          <NativeSelectOption value="">—</NativeSelectOption>
          {(field.options.choices ?? []).map((c) => (
            <NativeSelectOption key={c.key} value={c.key}>
              {c.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </span>
    );
  }
  if (field.type === "multi_select") {
    const keys = Array.isArray(value) ? (value as string[]) : [];
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <button
              id={id}
              type="button"
              aria-label={`${field.name}: ${fieldText(field, value)}`}
              className="flex min-h-7 min-w-0 flex-wrap items-center gap-1 rounded-md px-1 text-left text-sm hover:bg-muted"
            />
          }
        >
          {keys.length ? (
            keys.map((k) => {
              const c = field.options.choices?.find((x) => x.key === k);
              return <TagChip key={k} name={c?.name ?? k} color={chipColor(c?.color)} />;
            })
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-48">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{field.name}</DropdownMenuLabel>
            {(field.options.choices ?? []).map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={keys.includes(c.key)}
                onCheckedChange={(on) =>
                  onSave(on ? [...keys, c.key] : keys.filter((k) => k !== c.key))
                }
              >
                {c.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  if (field.type === "seat")
    return (
      <NativeSelect
        id={id}
        size="sm"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onSave(e.target.value || null)}
      >
        <NativeSelectOption value="">—</NativeSelectOption>
        {seats.map((s) => (
          <NativeSelectOption key={s.seatId} value={s.seatId}>
            {s.name}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    );
  if (field.type === "checkbox")
    return (
      <Checkbox id={id} checked={value === true} onCheckedChange={(v) => onSave(Boolean(v))} />
    );
  const numeric = field.type === "number" || field.type === "currency";
  const commit = () => {
    const next = fromDraft(field, value, draft);
    if (next === undefined) return setDraft(asDraft(field, value));
    if (draft.trim() !== asDraft(field, value)) onSave(next);
  };
  return (
    <span className="flex min-w-0 items-center gap-0.5">
      <Input
        id={id}
        className="h-7 min-w-0 px-2"
        type={
          numeric
            ? "number"
            : field.type === "date"
              ? "date"
              : field.type === "url"
                ? "url"
                : "text"
        }
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setDraft(asDraft(field, value));
        }}
      />
      {field.type === "url" && typeof value === "string" && /^https?:\/\//.test(value) ? (
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={`Open ${field.name}`}
          render={<a href={value} target="_blank" rel="noopener noreferrer" />}
        >
          <ExternalLinkIcon aria-hidden="true" />
        </Button>
      ) : null}
      {field.type === "text" && draft.length > LONG_TEXT ? (
        <Popover>
          <PopoverTrigger
            render={<Button variant="ghost" size="icon-xs" aria-label={`Expand ${field.name}`} />}
          >
            <Maximize2Icon aria-hidden="true" />
          </PopoverTrigger>
          <PopoverContent className="w-96">
            <Textarea
              aria-label={field.name}
              rows={8}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
            />
          </PopoverContent>
        </Popover>
      ) : null}
    </span>
  );
}

function FieldHistoryButton({
  api,
  co,
  taskId,
  field,
}: {
  api: Api;
  co: string;
  taskId: string;
  field: FieldDef;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`${field.name} history`}
            className="ml-auto shrink-0 opacity-0 group-hover/props:opacity-100 focus-visible:opacity-100 [[data-slot=task-field]:hover_&]:opacity-100"
          />
        }
      >
        <HistoryIcon aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent className="w-80">
        {open ? <FieldHistoryList api={api} co={co} taskId={taskId} field={field} /> : null}
      </PopoverContent>
    </Popover>
  );
}

function FieldHistoryList({
  api,
  co,
  taskId,
  field,
}: {
  api: Api;
  co: string;
  taskId: string;
  field: FieldDef;
}) {
  const { people } = useTaskHost();
  const q = useQuery(fieldHistoryQuery(api, co, taskId, field.id));
  const rows = q.data?.data ?? [];
  return (
    <div data-slot="field-history">
      <p className="mb-2 text-sm font-medium">{field.name}: history</p>
      {q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Never changed.</p>
      ) : (
        <ul aria-label={`${field.name} changes`} className="m-0 flex list-none flex-col p-0">
          {rows.map((r) => {
            const who = people.nameOf(r.actor.membership_id ?? null);
            return (
              <li key={r.event_id} className="flex flex-col border-b border-subtle py-1.5 text-sm">
                <span>
                  <span className="text-muted-foreground line-through">
                    {fieldText(field, r.from, (s) => people.of(s).name)}
                  </span>{" "}
                  → {fieldText(field, r.to, (s) => people.of(s).name)}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {who.kind === "agent" || r.actor.kind === "digital_employee" ? (
                    <BotIcon aria-hidden="true" className="size-3" />
                  ) : null}
                  {r.actor.kind === "system" ? "Automation" : who.name} ·{" "}
                  {new Date(r.at).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Whether a field shows without being revealed. */
function shown(f: FieldDef, task: TaskDetail, rule: FieldRule | undefined, missing: Set<string>) {
  return (
    !empty(task.fields[f.id]) ||
    rule?.always_show === true ||
    (rule?.required_on ?? "none") !== "none" ||
    missing.has(f.id)
  );
}

/**
 * The properties column's fields: those the body does not place (`placed` holds the field ids
 * the layout puts in the work pane).
 */
export function FieldsSection({
  api,
  company,
  task,
  fields,
  placed = new Set<string>(),
  canEdit = true,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  fields: FieldDef[];
  placed?: Set<string>;
  canEdit?: boolean;
}) {
  const { rules, missing } = useTaskHost();
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [agentOpen, setAgentOpen] = useState(false);
  const mine = fields.filter((f) => !placed.has(f.id));
  if (!mine.length) return null;
  const visible = (f: FieldDef) => revealed.has(f.id) || shown(f, task, rules.get(f.id), missing);
  const people = mine.filter((f) => f.audience !== "agent");
  const agents = mine.filter((f) => f.audience === "agent");
  const hidden = people.filter((f) => !visible(f) && !READ_ONLY.has(f.type));
  const line = (f: FieldDef) => (
    <FieldLine key={f.id} api={api} company={company} task={task} field={f} canEdit={canEdit} />
  );
  return (
    <section
      aria-label="Fields"
      data-slot="task-fields"
      className="group/props mt-4 flex flex-col gap-0.5 border-t border-subtle pt-3"
    >
      <h2 className="mb-1 text-xs font-medium text-muted-foreground">Fields</h2>
      {people.filter(visible).map(line)}
      {hidden.length && canEdit ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 w-fit gap-1.5 text-muted-foreground hover:text-foreground"
              />
            }
          >
            <PlusIcon aria-hidden="true" />
            Set a field
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-48">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Fields</DropdownMenuLabel>
              {hidden.map((f) => (
                <DropdownMenuItem
                  key={f.id}
                  onClick={() => {
                    setRevealed((s) => new Set(s).add(f.id));
                    // Focus its editor once it renders.
                    requestAnimationFrame(() =>
                      document
                        .querySelector<HTMLElement>(
                          `[data-slot=task-field][data-field="${CSS.escape(f.name)}"] input, [data-slot=task-field][data-field="${CSS.escape(f.name)}"] select, [data-slot=task-field][data-field="${CSS.escape(f.name)}"] button`,
                        )
                        ?.focus(),
                    );
                  }}
                >
                  {f.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {agents.length ? (
        <div data-slot="agent-fields" className="mt-2">
          <button
            type="button"
            aria-expanded={agentOpen}
            className="-ml-1 flex items-center gap-1 rounded-sm px-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setAgentOpen((o) => !o)}
          >
            {agentOpen ? (
              <ChevronDownIcon aria-hidden="true" className="size-3.5" />
            ) : (
              <ChevronRightIcon aria-hidden="true" className="size-3.5" />
            )}
            <BotIcon aria-hidden="true" className="size-3.5" />
            Agent fields ({agents.length})
          </button>
          {agentOpen || agents.some((f) => missing.has(f.id)) ? (
            <div className="mt-1 flex flex-col gap-0.5">{agents.map(line)}</div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
